<template>
	<section class="marketing-section bg-color-greige">
		<div class="container">
			<h2 class="heading-xl text-align-center history-of-markets-header ml-auto mr-auto">
				The history of public markets is the future of private markets
			</h2>
			<section class="py-300 py-500-md">
				<div class="col-8-md col-offset-2-md mb-250">
					<h3 class="heading-1 text-align-center-md mb-100 mb-250-md">Inevitable disruption of technology</h3>
					<p>
						The private markets are similar today to the stock market of the previous century. Just as the
						raucous trading pits were replaced by silent machines, the same automation is coming for private
						markets. Today, everything is done by people, from the sales to closings to operations.
						Tomorrow, software will replace inefficient, repetitive manual work.
					</p>
					<p>
						Technology companies know that to automate a process, you must first standardize it. A near
						impossibility if your customer is a billion-dollar enterprise that expects bespoke solutions.
					</p>
					<p>
						If your customers are millions of individuals, standardization is a necessity.<br />
						It’s why democracy unlocks technology.
					</p>
				</div>
				<img
					v-lazy="tradingFloorDataCenterImagePath"
					class="ipo-trading-floor-image"
					alt="An image of the NYSE trading floor in 1987 crowded with traders follow by an image of a high frequency trading data center with a room full of servers and no people in 2023"
				/>
			</section>
			<section class="display-flex-md align-items-center grid-md bt marketing-section">
				<div class="col-6-md mr-300-md">
					<h3 class="heading-1"> Our growth flywheel </h3>
					<p
						>More scale allows for greater technology investment which lowers costs and enhances quality.
						Success begets growth, and our flywheel moves faster. The more we integrate technology, the more
						its compounding power unlocks additional growth.</p
					>
				</div>
				<div class="col-6-md display-flex-md align-items-center justify-content-space-between gap-150">
					<img
						v-lazy="flywheelImagePath"
						class="ipo-growth-flywheel-image mr-auto ml-auto mt-300-smo"
						alt="A circle of arrows with labels 'More investors and assets', 'Increased technology leverage', 'Better outcomes for investors'"
					/>
					<div class="mt-150-smo text-color-content-secondary">
						<h4 class="body-md font-weight-bold">Example network effects:</h4>
						<ul class="ul mt-25 example-network-effects-list">
							<li>More efficient, lower-cost operations</li>
							<li>Lower cost expands access</li>
							<li>Increased data and denominator effects</li>
						</ul>
					</div>
				</div>
			</section>
			<section class="grid-md marketing-section display-flex flex-direction-column-smo pb-0 bt">
				<div class="col-6-md expanding-access-image mt-300-smo">
					<OfferingsIpoAllocationAlternatives />
				</div>

				<div class="col-6-md">
					<p class="overline mb-50">The Opportunity</p>
					<h3 class="heading-1">Expanding access to tens of millions of investors</h3>
					<p class="mt-150"
						>Institutional investors invest 20 - 30% of their portfolio in private markets to improve
						performance. On the other hand, most individuals have been practically barred from private
						investing. These barriers have been both regulatory and economic.</p
					>
					<p
						>That’s left tens of millions of individuals excluded from investing in private assets.
						Depending on your definition of target customer, there are between 35 and 100 million potential
						investors with $30 trillion+ of net worth that could be allocated to the private markets — the
						total addressable market could hardly be larger.</p
					>
				</div>
			</section>
		</div>
	</section>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import OfferingsIpoAllocationAlternatives from '@components/offerings/ipo/offerings-ipo-allocation-alternatives.vue';
import { useAppStore } from '@stores/app';

const appStore = useAppStore();

const baseIpoImagePath = `${appCloudfrontPath}/images/offerings/fundrise-ipo`;
const flywheelImagePath = `${baseIpoImagePath}/growth-flywheel.png`;

const tradingFloorDataCenterImagePath = computed((): string => {
	return `${baseIpoImagePath}/trading-floor-data-center${appStore.isMobile ? '-mobile' : ''}.png`;
});
</script>

<style lang="scss" scoped>
@use '../../../styles/utilities/respond-to.scss' as *;

.ipo-trading-floor-image {
	width: 100%;
	max-width: 924px;
	margin-left: auto;
	margin-right: auto;
}

.ipo-growth-flywheel-image {
	max-width: 16rem;

	@include respond-to(md) {
		max-width: 50%;
	}
}

.history-of-markets-header {
	max-width: 47rem;
}

.expanding-access-image {
	@include respond-to(smo) {
		order: 2;
	}
}

ul.ul.example-network-effects-list > li {
	margin-left: 0.6rem;
}
</style>
